import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ProviderLayout from '../../components/ProviderLayout';
import { apiFetch } from '../../api';
import { formatDateTime } from '../../lib/datetime';
import Icon from '../../components/Icon';
import { orderStatusLabel } from '../../lib/orderStatus';
import { formatOrderDescription } from '../../lib/orderDescription';


function asArr(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.items)) return d.items;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
}

// 404 ("hali yo'q") xato emas — bo'sh ro'yxat; boshqa !ok javoblar xato.
async function readList(settled) {
  if (settled.status !== 'fulfilled') return { ok: false, items: [] };
  const res = settled.value;
  if (res.status === 404) return { ok: true, items: [] };
  if (!res.ok) return { ok: false, items: [] };
  const data = await res.json().catch(() => null);
  return { ok: true, items: asArr(data) };
}

function budgetLabel(b) {
  if (b == null) return 'Kelishilgan';
  return `${b} so'm`;
}

export default function ProviderOrdersPage() {
  const { auth } = useAuth();
  const [segment, setSegment] = useState('feed'); // 'feed' | 'active'
  const [feed, setFeed] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load both independently so one failure doesn't blank the screen.
      const [feedRes, ordersRes] = await Promise.allSettled([
        apiFetch('/api/provider/tasks/feed', auth.token),
        apiFetch('/api/provider/orders/active', auth.token),
      ]);
      const [f, o] = await Promise.all([readList(feedRes), readList(ordersRes)]);
      setFeed(f.items);
      setOrders(o.items);
      if (!f.ok && !o.ok) setError('Buyurtmalarni yuklashda xatolik yuz berdi.');
    } catch {
      setError('Buyurtmalarni yuklashda xatolik yuz berdi.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const list = segment === 'feed' ? feed : orders;

  return (
    <ProviderLayout title="Buyurtmalar">
      <div style={s.segRow}>
        <Seg label="Yangi vazifalar" active={segment === 'feed'} onClick={() => setSegment('feed')} />
        <Seg label="Buyurtmalarim" active={segment === 'active'} onClick={() => setSegment('active')} />
      </div>

      {loading ? (
        <div style={s.center}>Yuklanmoqda...</div>
      ) : error ? (
        <div style={s.error}>{error}</div>
      ) : list.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <Icon name="package" size={40} color="var(--color-primary)" />
          </div>
          <p style={s.emptyText}>
            {segment === 'feed' ? "Hozircha mos vazifa yo'q" : "Aktiv buyurtma yo'q"}
          </p>
          <p style={s.emptyHint}>Yangi vazifalar shu yerda paydo bo'ladi.</p>
        </div>
      ) : (
        list.map((item, i) => (
          <div key={item.id ?? item.taskId ?? i} style={s.card}>
            <div style={s.cardTop}>
              <span style={s.cardTitle}>
                {item.customerName || item.categoryName || `Vazifa #${item.taskId ?? item.id}`}
              </span>
              {item.status && <span style={s.badge}>{orderStatusLabel(item.status)}</span>}
            </div>
            {item.description && <p style={s.cardDesc}>{formatOrderDescription(item.description)}</p>}
            {item.address && <p style={s.meta}>{`Manzil: ${item.address}`}</p>}
            <p style={s.budget}>{budgetLabel(item.budget ?? item.price ?? item.amount)}</p>
            {formatDateTime(item.createdAt) && <p style={s.date}>{formatDateTime(item.createdAt)}</p>}
          </div>
        ))
      )}
    </ProviderLayout>
  );
}

function Seg({ label, active, onClick }) {
  return (
    <button style={{ ...s.seg, ...(active ? s.segActive : {}) }} onClick={onClick}>
      <span style={{ color: active ? 'var(--color-on-primary)' : 'var(--color-text)', fontSize: '13px', fontWeight: active ? '700' : '500' }}>
        {label}
      </span>
    </button>
  );
}

const s = {
  segRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  seg: {
    flex: 1,
    padding: '10px',
    borderRadius: 'var(--radius-btn)',
    background: 'var(--color-surface-alt)',
    border: 'none',
    cursor: 'pointer',
  },
  segActive: { background: 'var(--color-primary)' },
  center: { textAlign: 'center', padding: '48px', color: 'var(--color-text-muted)' },
  error: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-input)',
    padding: '12px 14px',
    color: 'var(--color-danger)',
    fontSize: '14px',
  },
  empty: { textAlign: 'center', padding: '48px 16px' },
  emptyIcon: { display: 'flex', justifyContent: 'center', marginBottom: '10px' },
  emptyText: { margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  emptyHint: { margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' },
  card: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '16px',
    marginBottom: '8px',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: 'var(--color-text)' },
  badge: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    borderRadius: 'var(--radius-chip)',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  cardDesc: { fontSize: '15px', color: 'var(--color-text)', margin: '0 0 6px', lineHeight: 1.4 },
  meta: { fontSize: '13px', color: 'var(--color-text-muted)', margin: '0 0 4px' },
  budget: { fontSize: '15px', fontWeight: '700', color: 'var(--color-text)', margin: '4px 0 0' },
  date: { fontSize: '12px', color: 'var(--color-text-muted)', margin: '4px 0 0' },
};
